import { Close } from "@mui/icons-material";
import { Alert, AlertColor, IconButton, Snackbar, SnackbarCloseReason } from "@mui/material";
import React, { FC } from "react";
import theme from "theme";

/**
 * Component props
 */
interface Props {
  open: boolean;
  onClose: () => void;
  autoHideDuration?: number;
  severity: AlertColor;
  variant?: "standard" | "filled" | "outlined";
  children?: JSX.Element;
  disableClickaway?: boolean;
}

/**
 * Generic Snackbar component
 */
const GenericSnackbar: FC<Props> = ({
  open,
  onClose,
  autoHideDuration,
  severity,
  variant,
  disableClickaway,
  children
}) => {
  /**
   * Handles Snackbar close
   */
  const handleClose = (reason: SnackbarCloseReason) => {
    if (disableClickaway && reason !== "timeout") {
      return;
    }

    onClose();
  };

  const style: React.CSSProperties = {};
  if (severity === "success") {
    style.borderTop = `5px solid ${theme.palette.success.main}`;
    style.color = theme.palette.success.main;
  }
  if (severity === "error") {
    style.borderTop = `5px solid ${theme.palette.error.main}`;
    style.color = theme.palette.error.main;
  }
  if (severity === "warning") {
    style.borderTop = `5px solid ${theme.palette.warning.main}`;
    style.color = theme.palette.warning.main;
  }

  return (
    <Snackbar
      open={ open }
      onClose={ (e, reason) => handleClose(reason) }
      autoHideDuration={ autoHideDuration ?? null }
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        severity={ severity }
        variant={ variant ?? "outlined" }
        elevation={ 0 }
        sx={{
          alignItems: "center",
          backgroundColor: "#ffffff",
          border: `1px solid ${severity === "success" ? "#9acdbb" : "#d3d8d5"}`,
          borderRadius: 1,
          boxShadow: "0 8px 24px rgba(21, 49, 39, 0.16)",
          color: "#17382c",
          maxWidth: "calc(100vw - 32px)",
          minWidth: 0,
          padding: "8px 10px",
          width: "auto",
          "& .MuiAlert-icon": {
            alignItems: "center",
            fontSize: 23,
            marginRight: 1,
            padding: 0
          },
          "& .MuiAlert-message": {
            minWidth: 0,
            padding: "2px 0"
          },
          "& .MuiAlertTitle-root": {
            color: "inherit",
            fontSize: 14,
            fontWeight: 600,
            lineHeight: "20px",
            margin: 0
          }
        }}
        style={ style }
        action={
          <IconButton
            onClick={ () => handleClose("timeout") }
            aria-label="Close notification"
            size="small"
            sx={{ color: "#557068", marginLeft: 0.5 }}
          >
            <Close/>
          </IconButton>
        }
      >
        { children }
      </Alert>
    </Snackbar>
  );
};

export default GenericSnackbar;